import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';   // Eye icons
import './authStyles/Login.css';
import { setUserData, syncMainBoardPrefsToLocalStorage, retrieveMainBoardCustomizations } from '../../supplementary_components/userDataDelegate';
import { removeCompanyData, retrieveCompanyData, setCompany } from '../../supplementary_components/companySettings';
import { API_ROOT } from '../../supplementary_components/requests';
import { CustomLoadingIcon } from '../../supplementary_components/loading_icon';
function isCompanyAdminUser(user) {
    return user?.roleCode === 'COMPANY_ADMIN' || user?.permissions?.COMPANY_ADMIN === true;
}
function Login() {
    const [loading, setLoading] = useState(false);
    const [carrierId, setCarrierId] = useState(localStorage.getItem('carrier_identifier') || '');
    const [login, setLogin] = useState(localStorage.getItem('current_username') || '');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [showPassword, setShowPassword] = useState(false); // State to toggle password visibility
    const navigate = useNavigate();

    const handleLogin = async (e) => {
        e.preventDefault();
        if(loading){
            return;
        }
        setLoading(true);
        setError(null);
        const formLogin = e.target.login?.value ?? login;
        const formPassword = e.target.password?.value ?? password;
        try {
            const response = await fetch(API_ROOT+'auth/authenticate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ login: formLogin, password: formPassword, carrierIdentifier: carrierId }),
                signal: AbortSignal.timeout(60000),
            });

            if (response.ok) {
                setLoading(false);
                const data = await response.json();
                const orgId = data.org_id;

                localStorage.setItem('access_token', data.access_token); // Store the access token
                localStorage.setItem('refresh_token', data.refresh_token); // Store the refresh token
                localStorage.setItem('current_username', formLogin);
                localStorage.setItem('carrier_identifier', carrierId); // Remember MC/DOT for next login
                const user = data.user;
                const landingCompanyId = data.company?.id ?? user.company?.id;
                const applyCompany = (company) => {
                    setCompany(company);
                    if (company?.id != null) {
                        syncMainBoardPrefsToLocalStorage(retrieveMainBoardCustomizations(), company.id);
                    }
                };
                if(data.company){
                    // Land directly on the company the MC/DOT belongs to
                    applyCompany(data.company);
                }else if(user.accessibleCompanies === null){
                    if(isCompanyAdminUser(user)){
                        removeCompanyData();
                    }
                }else{
                    const firstCompany = user.accessibleCompanies[0];
                    const savedCompany = retrieveCompanyData();
                    if (savedCompany.id){
                        let matched = null;
                        for(let entry of user.accessibleCompanies){
                            const parsed = entry;
                            if(parseInt(parsed.id) === parseInt(savedCompany.id) && parsed.companyName === savedCompany.companyName && parsed.firstDayOfAWeek === savedCompany.firstDayOfAWeek && ("mainlyOperatesWithSolo" in savedCompany && savedCompany.mainlyOperatesWithSolo === parsed.mainlyOperatesWithSolo)){
                                matched = parsed;
                                break;
                            }
                        }
                        applyCompany(matched || firstCompany);
                    }else{
                        applyCompany(firstCompany);
                    }
                }
                // Company flag must be in localStorage before column normalize runs.
                setUserData(user, orgId, landingCompanyId);
                if(user.passwordIsTemporary){
                    localStorage.setItem('temp_password', formPassword);
                    navigate('/reset-password'); 
                }else{
                    localStorage.removeItem('temp_password');
                    navigate('/dashboard'); 
                }
            } else {
                // Handle error response
                setLoading(false);
                const errorData = await response.json().catch(() => ({}));
                // 404 => carrier (MC/DOT) not found; otherwise the org was resolved,
                // so remember MC/DOT and login for the next attempt.
                if(response.status !== 404){
                    localStorage.setItem('carrier_identifier', carrierId);
                    localStorage.setItem('current_username', formLogin);
                }
                setError(errorData.message || 'Login failed');
            }
        } catch (error) {
            setLoading(false);
            console.error('Error during login:', error);
            if (error?.name === 'TimeoutError' || error?.name === 'AbortError') {
                setError('Login timed out. Please try again.');
            } else {
                setError('Something went wrong. Please try again.');
            }
        }
    };
    return (
        <div className="loginFormWrapper">
            <div className="loginFormBox">
                <div className="pageLabel">SurfTMS</div>
                {error && <p className="error-message">{error}</p>}
                <form onSubmit={handleLogin}>
                    <div className="input-group">
                        <label htmlFor="carrierId">MC or DOT Number:</label>
                        <input
                            type="text"
                            id="carrierId"
                            value={carrierId ?? ''}
                            onChange={(e) => setCarrierId(e.target.value)}
                            required
                        />
                    </div>
                    <div className="input-group">
                        <label htmlFor="login">Login:</label>
                        <input
                            type="text"
                            id="login"
                            value={login}
                            onChange={(e) => setLogin(e.target.value)}
                            autoComplete="off"
                            required
                        />
                    </div>
                    <div className="input-group password-group">
                        <label htmlFor="password">Password:</label>
                        <input
                            type={showPassword ? 'text' : 'password'} // Toggle between password and text input types
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                        />
                        <span
                            className="password-toggle-icon"
                            onClick={() => setShowPassword(!showPassword)} // Toggle password visibility
                        >
                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                        </span>
                    </div>
                    <div className="submit-group" >
                        <button type="submit" disabled={loading} >Login</button>
                        {loading?<CustomLoadingIcon height={'1.2rem'} speed={1.0} strokeColor="white" />:null}
                    </div>
                    <div className="forgot-password">
                        <Link to="/forgot-password">Forgot/Reset password</Link>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default Login;
